import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CheckGlyph} from '@/components/ui/icons'
import {MicroLabel} from '@/components/period-selector'
import {MarcarEjecucionDialog} from '@/components/marcar-ejecucion-dialog'
import {useDesmarcarEjecucion, type PlanActividad, type PlanTrabajo} from '@/hooks/sgsst/plan/use-plan-trabajo'
import {cn} from '@/lib/utils'

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

function Leyenda() {
  const {t} = useTranslation()
  return (
    <div
      className="flex flex-wrap items-center gap-4 text-[12px] text-muted-foreground"
      data-testid="cronograma-leyenda">
      <span className="font-mono text-[10px] tracking-wider uppercase">
        {t('planTrabajo.cronograma.leyenda.titulo')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block h-4 w-4 rounded-full border border-primary/50" aria-hidden="true" />
        {t('planTrabajo.cronograma.leyenda.planeado')}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground"
          aria-hidden="true">
          <CheckGlyph size={10} />
        </span>
        {t('planTrabajo.cronograma.leyenda.ejecutado')}
      </span>
      <span className="flex items-center gap-1.5">
        <span className="tone-amber inline-block h-4 w-4 rounded-full" aria-hidden="true" />
        {t('planTrabajo.cronograma.leyenda.vencido')}
      </span>
    </div>
  )
}

export function Cronograma({
  businessId,
  plan,
  onAbrirDetalle,
}: {
  businessId: string
  plan: PlanTrabajo
  onAbrirDetalle: (actividadId: string) => void
}) {
  const {t} = useTranslation()
  const desmarcar = useDesmarcarEjecucion(businessId, plan.id)
  const [marcando, setMarcando] = useState<{actividad: PlanActividad; mes: number} | null>(null)

  return (
    // El scroll horizontal es solo de la tabla: así la leyenda no se va de pantalla en móvil.
    <div id="cronograma-plan" className="surface overflow-hidden" data-testid="cronograma">
      <div className="surface-divider border-b px-4 py-2.5">
        <Leyenda />
      </div>
      <div className="overflow-x-auto p-4">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr>
              <th className="pb-2 pr-3">
                <MicroLabel>{t('planTrabajo.cronograma.actividad')}</MicroLabel>
              </th>
              <th
                colSpan={12}
                className="pb-1 text-center font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
                {plan.period_year}
              </th>
            </tr>
            <tr>
              <th className="pb-2 pr-3" />
              {MESES.map((m, i) => (
                <th key={i} className="px-1 pb-2 text-center font-mono text-[11px] tabular-nums text-muted-foreground">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {plan.actividades.map((act) => (
              <CronogramaRow
                key={act.id}
                act={act}
                onAbrirDetalle={() => onAbrirDetalle(act.id)}
                onSolicitarMarcar={(mes) => setMarcando({actividad: act, mes})}
                onDesmarcar={(mes) => desmarcar.mutate({actividadId: act.id, mes})}
              />
            ))}
          </tbody>
        </table>
      </div>
      {marcando && (
        <MarcarEjecucionDialog
          businessId={businessId}
          planId={plan.id}
          periodYear={plan.period_year}
          actividad={marcando.actividad}
          mes={marcando.mes}
          onClose={() => setMarcando(null)}
        />
      )}
    </div>
  )
}

function CronogramaRow({
  act,
  onAbrirDetalle,
  onSolicitarMarcar,
  onDesmarcar,
}: {
  act: PlanActividad
  onAbrirDetalle: () => void
  onSolicitarMarcar: (mes: number) => void
  onDesmarcar: (mes: number) => void
}) {
  const {t} = useTranslation()
  const planeados = new Set(act.meses_planeados)
  const ejecutados = new Set(act.meses_ejecutados)
  // nosemgrep: browser-clock-business-date -- current-month determination off the browser clock; tracked debt, not fixed in this wave (FE-02)
  const mesActual = new Date().getMonth() + 1

  return (
    <tr className="border-t border-border align-top" data-testid="cronograma-fila">
      <td className="py-2 pr-3">
        <button
          type="button"
          className="text-left text-[14px] font-medium text-foreground underline-offset-2 hover:underline"
          data-testid="cronograma-actividad-nombre"
          onClick={onAbrirDetalle}>
          {act.titulo}
        </button>
        {act.asignado_nombre && (
          <p className="mt-0.5 text-[12px] text-muted-foreground" data-testid="cronograma-asignado">
            {t('planTrabajo.cronograma.asignadoA', {name: act.asignado_nombre})}
          </p>
        )}
        {act.cita && <p className="mt-0.5 font-mono text-[10.5px] text-muted-foreground">{act.cita}</p>}
      </td>
      {Array.from({length: 12}, (_, i) => i + 1).map((mes) => {
        const isP = planeados.has(mes)
        const isE = ejecutados.has(mes)
        const vencido = isP && !isE && mes < mesActual
        return (
          <td key={mes} className="px-1 py-2 text-center">
            {isE ? (
              <button
                type="button"
                onClick={() => onDesmarcar(mes)}
                data-testid="celda-ejecutado"
                aria-label={t('planTrabajo.cronograma.desmarcar')}
                className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/85 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
                {/* 14 y no 12: a 12 px el trazo de `CheckGlyph` queda demasiado fino en círculos de 24 px. */}
                <CheckGlyph size={14} />
              </button>
            ) : isP ? (
              // También los meses futuros: el backend valida la fecha de ejecución, no el mes.
              <button
                type="button"
                onClick={() => onSolicitarMarcar(mes)}
                data-testid="celda-planeado"
                // El ámbar es la única marca de vencido: el nombre accesible lo dice sin color (U17).
                aria-label={
                  vencido
                    ? t('planTrabajo.cronograma.marcarVencido', {mes: MESES[mes - 1]})
                    : t('planTrabajo.cronograma.marcar')
                }
                className={cn(
                  'inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-2 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  vencido ? 'tone-amber border-transparent' : 'border-primary/50'
                )}
              />
            ) : (
              <button
                type="button"
                aria-disabled="true"
                data-testid="celda-no-planeado"
                aria-label={t('planTrabajo.cronograma.noPlaneado', {mes: MESES[mes - 1]})}
                className="inline-flex h-6 w-6 cursor-help items-center justify-center rounded-full text-muted-foreground/30">
                ·
              </button>
            )}
          </td>
        )
      })}
    </tr>
  )
}
